import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Collapse,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tooltip,
  Typography,
  Button,
  Stack,
  TextField,
  Chip,
  InputAdornment
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const itemHeadCells = [
  { id: 'itemId', label: 'Item ID' },
  { id: 'shippingInstructionId', label: 'SI' },
  { id: 'customerGradeMark', label: 'Customer Grade Mark' },
  { id: 'itemName', label: 'Item Name' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'cases', label: 'Cases' },
  { id: 'percentage', label: '%' },
  { id: 'nicotine', label: 'Nicotine' },
  { id: 'nicotineContribution', label: 'Nicotine %' },
  { id: 'sugar', label: 'Sugar' },
  { id: 'sugarContribution', label: 'Sugar %' },
  { id: 'unit', label: 'Unit' },
  { id: 'actions', label: 'Actions', align: 'center' }
];

const calculateQuantityStats = (items) => {
  if (!items || items.length === 0) return {
    total: 0,
    totalNicotine: 0,
    totalSugar: 0,
    totalCases: 0,
    itemsWithPercentages: []
  };

  const total = items.reduce((sum, item) => {
    const qty = Math.abs(Number(item.quantity)) || 0;
    return sum + qty;
  }, 0);

  const itemsWithPercentages = items.map(item => {
    const quantity = Math.abs(Number(item.quantity)) || 0;
    const percentage = total > 0 ? (quantity / total) * 100 : 0;
    const nicotineValue = Number(item.nicotine) || 0;
    const sugarValue = Number(item.sugar) || 0;
    const nicotineContribution = (percentage * nicotineValue) / 100;
    const sugarContribution = (percentage * sugarValue) / 100;

    const netWeightPerCase = Number(item.netWeightPerCase) || 0;
    const cases = netWeightPerCase > 0 ? Math.round(quantity / netWeightPerCase) : 0;

    return {
      ...item,
      percentage: percentage.toFixed(2),
      cases,
      nicotineContribution: nicotineContribution.toFixed(2),
      sugarContribution: sugarContribution.toFixed(2)
    };
  });

  const totalNicotine = itemsWithPercentages.reduce(
    (sum, item) => sum + Number(item.nicotineContribution), 0
  ).toFixed(2);

  const totalSugar = itemsWithPercentages.reduce(
    (sum, item) => sum + Number(item.sugarContribution), 0
  ).toFixed(2);

  const totalCases = itemsWithPercentages.reduce((sum, item) => sum + (item.cases || 0), 0);

  return {
    total: Math.abs(total).toFixed(2),
    totalNicotine,
    totalSugar,
    totalCases,
    itemsWithPercentages
  };
};

export default function VersionAccordion({
  version,
  items,
  expanded,
  hasActiveBatch,
  // Item filter state (shared across all accordions)
  itemFilter,
  setItemFilter,
  showItemFilters,
  setShowItemFilters,
  hasActiveItemFilters,
  clearItemFilters,
  getFilteredItems,
  // Callbacks
  onExpand,
  onEditVersion,
  onDuplicateVersion,
  onDeleteVersion,
  onAddItem,
  onEditItem,
  onDeleteItem
}) {
  const batchLockMsg = hasActiveBatch
    ? 'Blend items cannot be modified while an active batch exists for this version.'
    : '';
  const v = version;
  const filteredItems = getFilteredItems(v.id);
  const { total, totalNicotine, totalSugar, totalCases, itemsWithPercentages } = calculateQuantityStats(filteredItems);
  const nicotineTolerancePct = Number(v?.nicotineTolerancePct) || 0;
  const sugarTolerancePct = Number(v?.sugarTolerancePct) || 0;
  const nicotineTargetMin = (Number(totalNicotine) || 0) * (1 - nicotineTolerancePct / 100);
  const sugarTargetMin = (Number(totalSugar) || 0) * (1 - sugarTolerancePct / 100);

  return (
    <Accordion
      expanded={!!expanded}
      onChange={(_event, expanded) => {
        onExpand(v.id, expanded);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
          <Typography>
            Version: {v.versionName} — Created: {new Date(v.timestamp).toLocaleDateString()}
          </Typography>
          <Chip
            label={v.status || 'Actual'}
            size="small"
            color={v.status === 'Sample' ? 'warning' : 'primary'}
            variant="outlined"
          />
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEditVersion(v); }}
              title="Edit Version"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDuplicateVersion(v); }}
              title="Duplicate Version"
            >
              <ContentCopyIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteVersion(v.id); }}
              title="Delete Version"
            >
              <DeleteIcon />
            </IconButton>
            <Tooltip title={batchLockMsg}>
              <span>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={(e) => { e.stopPropagation(); onAddItem(v.id); }}
                  disabled={hasActiveBatch}
                >
                  Add Item
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip size="small" variant="outlined" label={`Cut Width: ${v?.cutWidth || '-'}`} />
          <Chip size="small" variant="outlined" label={`Moisture: ${v?.moisture || '-'}`} />
          <Chip size="small" variant="outlined" label={`Temperature: ${v?.temperature || '-'}`} />
          <Chip size="small" color="primary" variant="outlined" label={`Nic Tol: ${nicotineTolerancePct.toFixed(2)}%`} />
          <Chip size="small" color="primary" variant="outlined" label={`Sugar Tol: ${sugarTolerancePct.toFixed(2)}%`} />
        </Stack>

        {/* Item Filters */}
        <Box mb={2}>
          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Typography variant="subtitle2">Items</Typography>
            {hasActiveItemFilters() && (
              <Chip
                size="small"
                label={`${filteredItems.length}/${items.length}`}
                color="primary"
                variant="outlined"
              />
            )}
            <IconButton
              size="small"
              onClick={() => setShowItemFilters(!showItemFilters)}
              color={showItemFilters || hasActiveItemFilters() ? 'primary' : 'default'}
            >
              <FilterListIcon />
            </IconButton>
          </Stack>
          <Collapse in={showItemFilters}>
            <Box p={2} bgcolor="grey.50" borderRadius={1}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Item ID"
                    value={itemFilter.itemId}
                    onChange={(e) => setItemFilter(prev => ({ ...prev, itemId: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Customer Grade Mark"
                    value={itemFilter.customerGradeMark}
                    onChange={(e) => setItemFilter(prev => ({ ...prev, customerGradeMark: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Min Quantity"
                    type="number"
                    value={itemFilter.minQuantity}
                    onChange={(e) => setItemFilter(prev => ({ ...prev, minQuantity: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Max Quantity"
                    type="number"
                    value={itemFilter.maxQuantity}
                    onChange={(e) => setItemFilter(prev => ({ ...prev, maxQuantity: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    size="small"
                    startIcon={<ClearIcon />}
                    onClick={clearItemFilters}
                    disabled={!itemFilter.itemId && !itemFilter.customerGradeMark && !itemFilter.minQuantity && !itemFilter.maxQuantity}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        {/* Items Table */}
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {itemHeadCells.map(h => (
                  <TableCell key={h.id} align="center">{h.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsWithPercentages.map(row => (
                <TableRow key={row.id}>
                  {itemHeadCells.slice(0, -1).map(h => (
                    <TableCell key={h.id} align="center">
                      {h.id === 'percentage' || h.id === 'nicotineContribution' || h.id === 'sugarContribution'
                        ? `${row[h.id]}`
                        : row[h.id]}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={batchLockMsg}>
                        <span>
                          <IconButton size="small" onClick={() => onEditItem(row, v)} disabled={hasActiveBatch}>
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={batchLockMsg}>
                        <span>
                          <IconButton size="small" onClick={() => onDeleteItem(v.id, row.id)} disabled={hasActiveBatch}>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {/* Total row */}
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell align="center">Total:</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{total}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{totalCases}</TableCell>
                <TableCell align="center">100%</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{totalNicotine}%</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{totalSugar}%</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.06)' }}>
                <TableCell align="center" colSpan={7} sx={{ fontWeight: 'bold' }}>
                  Tolerance Adjusted Minimums
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  {nicotineTargetMin.toFixed(2)}% (from {totalNicotine}% - {nicotineTolerancePct.toFixed(2)}%)
                </TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  {sugarTargetMin.toFixed(2)}% (from {totalSugar}% - {sugarTolerancePct.toFixed(2)}%)
                </TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}
